import { useCallback } from "react";

import { Asset } from "@/components/graphql/schema.graphql";
import { getForgeSessionUrl } from "@/components/inference/utils";
import { SessionFileFragment } from "@/features/forge/forge-session/queries.graphql";

import { useRequiredWorkspace } from "../useRequiredWorkspace";
import useShowSnackbar from "../useShowSnackbar";

type Linkable =
  | ({ __typename: "File" } & Pick<SessionFileFragment, "url">)
  | ({ __typename: "Asset" } & Pick<Asset, "id" | "path" | "sessionId">);

export const useCopyFileLink = () => {
  const { workspace } = useRequiredWorkspace();
  const { showSuccess, showError } = useShowSnackbar();

  const copyFileLink = useCallback(
    async (object: Linkable) => {
      let link = "";
      if (object.__typename === "File") {
        link = object.url;
      } else if (object.__typename === "Asset") {
        if (!object.sessionId) {
          showError("Could not copy link to item");
          return;
        }
        const path = getForgeSessionUrl({
          workspaceName: workspace.name,
          sessionId: object.sessionId,
          assetId: object.id,
        });

        const url = new URL(path, window.location.origin);
        link = url.toString();
      }

      try {
        await navigator.clipboard.writeText(link);
        showSuccess("Copied link to item");
      } catch {
        console.error("Could not copy link to item");
        showError("Could not copy link to item");
      }
    },
    [workspace, showSuccess, showError],
  );

  return { copyFileLink };
};
